import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Form,
  InputNumber,
  message,
  Row,
  Col,
  Statistic,
  Tooltip,
  Badge,
  Popover,
  Drawer,
  Timeline,
  Typography,
  Switch,
  Pagination
} from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  LineChartOutlined,
  TeamOutlined,
  PlusOutlined,
  StopOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { 
  trialUserService, 
  TrialUser, 
  TrialUsageStats, 
  ActivityRecord, 
  TrialUserFilter,
  TrialUserSort,
  TrialUserQueryParams
} from '../../services/trialUserService';

const { Search } = Input;
const { Option } = Select;
const { Text, Title } = Typography;

const TrialUserManagement: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [users, setUsers] = useState<TrialUser[]>([]);
  const [stats, setStats] = useState<TrialUsageStats | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Server-side filtering and sorting
  const [filters, setFilters] = useState<TrialUserFilter>({});
  const [sort, setSort] = useState<TrialUserSort>({ field: 'created_at', direction: 'desc' });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [total, setTotal] = useState<number>(0);
  
  // Legacy filters for UI (will be converted to server filters)
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [trialFilter, setTrialFilter] = useState<string>('all');
  const [includeExpired, setIncludeExpired] = useState(true);
  const [includeActive, setIncludeActive] = useState(true);
  
  // Modals
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TrialUser | null>(null);
  const [activityDrawerVisible, setActivityDrawerVisible] = useState(false);
  const [userActivities, setUserActivities] = useState<ActivityRecord[]>([]);
  
  const [form] = Form.useForm();

  // Convert legacy UI filters to server filters
  useEffect(() => {
    const serverFilters: TrialUserFilter = {};
    
    // Convert search text to name/email filter
    if (searchText.trim()) {
      if (searchText.includes('@')) {
        serverFilters.email = searchText.trim();
      } else {
        serverFilters.name = searchText.trim();
      }
    }
    
    // Convert status filter
    if (statusFilter !== 'all') {
      serverFilters.status = statusFilter as 'Active' | 'Inactive';
    }
    
    // Convert trial filter
    if (trialFilter !== 'all') {
      serverFilters.trial_status = trialFilter as 'active' | 'expired' | 'no_trial';
    }
    
    setFilters(serverFilters);
  }, [searchText, statusFilter, trialFilter]);

  useEffect(() => {
    loadData();
  }, [currentPage, pageSize, filters, sort]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      
      const queryParams: TrialUserQueryParams = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        sort: sort
      };
      

      
      const [usersData, statsData] = await Promise.all([
        trialUserService.searchTrialUsers(queryParams, token),
        trialUserService.getTrialUsageStats(token)
      ]);
      
      setUsers(usersData);
      setStats(statsData);
      // For now, we'll use the stats for total estimation
      // In a real app, the search API should return total count
      setTotal(statsData.total_trial_users);
    } catch (error: any) {
      console.error('Error loading data:', error);
      
      if (error.response?.status === 422) {
        console.error('Validation error details:', error.response?.data);
        message.error('Invalid request parameters. Please check filters and sorting.');
      } else {
        message.error('Failed to load trial user data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExtendTrial = async (values: { extension_days: number; reason?: string }) => {
    if (!selectedUser) return;
    
    try {
      const token = await getAccessTokenSilently();
      const result = await trialUserService.extendUserTrial({
        user_id: selectedUser.id,
        extension_days: values.extension_days,
        reason: values.reason
      }, token);
      
      if (result.success) {
        message.success(result.message);
        setExtendModalVisible(false);
        form.resetFields();
        loadData();
      } else {
        message.error('Failed to extend trial');
      }
    } catch (error) {
      message.error('Failed to extend trial');
      console.error('Error extending trial:', error);
    }
  };

  const handleDeactivateUser = async (user: TrialUser) => {
    try {
      const token = await getAccessTokenSilently();
      const result = await trialUserService.deactivateUser(user.id, token);
      if (result.success) {
        message.success(`User ${user.email} deactivated`);
        loadData();
      } else {
        message.error('Failed to deactivate user');
      }
    } catch (error) {
      message.error('Failed to deactivate user');
      console.error('Error deactivating user:', error);
    }
  };

  const handleReactivateUser = async (user: TrialUser) => {
    try {
      const token = await getAccessTokenSilently();
      const result = await trialUserService.reactivateUser(user.id, token);
      if (result.success) {
        message.success(`User ${user.email} reactivated`);
        loadData();
      } else {
        message.error('Failed to reactivate user');
      }
    } catch (error) {
      message.error('Failed to reactivate user');
      console.error('Error reactivating user:', error);
    }
  };

  const showUserActivity = async (user: TrialUser) => {
    setSelectedUser(user);
    setActivityDrawerVisible(true);
    
    try {
      const token = await getAccessTokenSilently();
      const activityData = await trialUserService.getUserDetailedActivity(user.email, 50, token);
      setUserActivities(activityData.activities);
    } catch (error) {
      message.error('Failed to load user activity');
      console.error('Error loading user activity:', error);
    }
  };

  // Client-side filtering is now handled by server-side API

  const renderRecentActivity = (activities: ActivityRecord[]) => {
    if (activities.length === 0) {
      return (
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>No recent activity</Text>
        </div>
      );
    }

    const lastActivity = activities[0];
    const lastActivityTime = trialUserService.formatDateRelative(lastActivity.created_at);
    
    return (
      <div style={{ textAlign: 'center' }}>
        <Popover
          content={
            <div style={{ maxWidth: 350 }}>
              <div style={{ marginBottom: 12, fontWeight: 'bold', borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                Recent Activity Details
              </div>
              {activities.slice(0, 5).map((activity, index) => (
                <div key={index} style={{ 
                  marginBottom: 12, 
                  padding: 8,
                  backgroundColor: index === 0 ? '#f6ffed' : '#fafafa',
                  borderRadius: 4,
                  border: index === 0 ? '1px solid #b7eb8f' : '1px solid #e8e8e8'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Text strong style={{ color: index === 0 ? '#389e0d' : '#333' }}>
                      {trialUserService.formatActivityType(activity.activity_type)}
                    </Text>
                    {index === 0 && <Tag color="green">Latest</Tag>}
                  </div>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {trialUserService.formatDateRelative(activity.created_at)}
                  </Text>
                  {activity.activity_data && Object.keys(activity.activity_data).length > 0 && (
                    <div style={{ marginTop: 4, fontSize: '11px', color: '#666' }}>
                      {JSON.stringify(activity.activity_data, null, 0).substring(0, 100)}
                      {JSON.stringify(activity.activity_data).length > 100 && '...'}
                    </div>
                  )}
                </div>
              ))}
              {activities.length > 5 && (
                <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                  ... and {activities.length - 5} more activities
                </Text>
              )}
            </div>
          }
          title={null}
          trigger="hover"
          placement="leftTop"
        >
          <div style={{ cursor: 'pointer' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '4px 8px',
              backgroundColor: '#f0f2f5',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
              minWidth: '120px'
            }}>
              <LineChartOutlined style={{ marginRight: 6, color: '#1890ff' }} />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 'bold', lineHeight: 1.2 }}>
                  {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
                </div>
                <div style={{ fontSize: '11px', color: '#666', lineHeight: 1.2 }}>
                  Last: {lastActivityTime}
                </div>
              </div>
            </div>
          </div>
        </Popover>
      </div>
    );
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log('Table change triggered:', { pagination, filters, sorter });
    
    // Handle pagination with safety checks
    if (pagination && typeof pagination.current === 'number') {
      setCurrentPage(pagination.current);
    }
    if (pagination && typeof pagination.pageSize === 'number') {
      setPageSize(pagination.pageSize);
    }
    
    // Handle sorting with comprehensive safety checks
    if (sorter && typeof sorter.field === 'string' && sorter.order) {
      // Validate that the field is one of the allowed backend fields
      const validFields = ['name', 'email', 'status', 'trial_expires_at', 'created_at', 'activity_count', 'days_remaining'];
      
      if (validFields.includes(sorter.field)) {
        const newSort = {
          field: sorter.field as any,
          direction: sorter.order === 'descend' ? 'desc' as const : 'asc' as const
        };
        console.log('Setting new sort:', newSort);
        setSort(newSort);
      } else {
        console.warn('Invalid sort field:', sorter.field);
      }
    }
  };

  const columns = [
    {
      title: 'User Details',
      key: 'name',
      width: 240,
      sorter: true,
      sortOrder: sort.field === 'name' ? (sort.direction === 'asc' ? 'ascend' : 'descend') as any : undefined,
      render: (record: TrialUser) => (
        <div style={{ minWidth: '220px' }}>
          <div style={{ fontWeight: 500, fontSize: '13px', marginBottom: '2px' }}>
            {record.name}
          </div>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>
            {record.email}
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <Tag color={record.status === 'Active' ? 'green' : 'red'} style={{ fontSize: '10px', padding: '0 4px', height: '18px', lineHeight: '18px' }}>
              {record.status}
            </Tag>
            <Tag color={trialUserService.getTrialStatusColor(record)} style={{ fontSize: '10px', padding: '0 4px', height: '18px', lineHeight: '18px' }}>
              {trialUserService.getTrialStatusText(record)}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Trial & Activity',
      key: 'trial_expires_at',
      width: 160,
      sorter: true,
      sortOrder: sort.field === 'trial_expires_at' ? (sort.direction === 'asc' ? 'ascend' : 'descend') as any : undefined,
      render: (record: TrialUser) => (
        <div style={{ minWidth: '140px' }}>
          <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
            {record.trial_expires_at 
              ? new Date(record.trial_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : 'No trial'
            }
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: record.activity_count > 0 ? '#f6ffed' : '#f5f5f5',
              border: `1px solid ${record.activity_count > 0 ? '#52c41a' : '#d9d9d9'}`,
              fontSize: '11px',
              fontWeight: 'bold',
              color: record.activity_count > 0 ? '#52c41a' : '#999'
            }}>
              {record.activity_count}
            </div>
            <span style={{ fontSize: '11px', color: '#666' }}>activities</span>
          </div>
          <div style={{ fontSize: '10px', color: '#999' }}>
            {record.last_activity 
              ? `Last: ${trialUserService.formatDateRelative(record.last_activity)}`
              : 'No activity'
            }
          </div>
        </div>
      ),
    },
    {
      title: 'Organization',
      key: 'organization',
      width: 120,
      render: (record: TrialUser) => (
        <div style={{ minWidth: '100px' }}>
          {record.has_organization_access ? (
            <Tooltip title={record.organization_names.join(', ')}>
              <Tag color="blue" style={{ fontSize: '10px', padding: '0 4px', height: '18px', lineHeight: '18px' }}>
                <TeamOutlined style={{ fontSize: '10px' }} /> {record.organization_names.length}
              </Tag>
            </Tooltip>
          ) : (
            <Tag color="gray" style={{ fontSize: '10px', padding: '0 4px', height: '18px', lineHeight: '18px' }}>
              None
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Recent',
      key: 'recent_activities',
      width: 100,
      render: (record: TrialUser) => (
        <div style={{ minWidth: '80px' }}>
          {renderRecentActivity(record.recent_activities)}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (record: TrialUser) => (
        <Space size="small">
          <Tooltip title="View activity">
            <Button 
              type="text" 
              size="small"
              icon={<EyeOutlined />} 
              onClick={() => showUserActivity(record)}
            />
          </Tooltip>
          
          <Tooltip title="Extend trial">
            <Button 
              type="text" 
              size="small"
              icon={<PlusOutlined />} 
              onClick={() => {
                setSelectedUser(record);
                setExtendModalVisible(true);
              }}
            />
          </Tooltip>
          
          {record.status === 'Active' ? (
            <Tooltip title="Deactivate">
              <Button 
                type="text" 
                size="small"
                danger 
                icon={<StopOutlined />} 
                onClick={() => {
                  Modal.confirm({
                    title: 'Deactivate User',
                    content: `Are you sure you want to deactivate ${record.email}?`,
                    onOk: () => handleDeactivateUser(record),
                  });
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Reactivate">
              <Button 
                type="text" 
                size="small"
                icon={<PlayCircleOutlined />} 
                onClick={() => handleReactivateUser(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Trial User Management</Title>
      
      {/* Statistics Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Trial Users"
                value={stats.total_trial_users.toLocaleString()}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Active Trials"
                value={stats.active_trial_users.toLocaleString()}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Conversion Rate"
                value={stats.trial_conversion_rate}
                suffix="%"
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Avg Activity per User"
                value={stats.average_activity_per_user}
                prefix={<LineChartOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Search
              placeholder="Search users..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Statuses</Option>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={trialFilter}
              onChange={setTrialFilter}
              style={{ width: '100%' }}
              placeholder="Filter by trial"
            >
              <Option value="all">All Trials</Option>
              <Option value="active">Active Trials</Option>
              <Option value="expired">Expired Trials</Option>
              <Option value="no_trial">No Trial</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Space>
              <span>Show:</span>
              <Switch
                checked={includeActive}
                onChange={setIncludeActive}
                checkedChildren="Active"
                unCheckedChildren="Active"
              />
              <Switch
                checked={includeExpired}
                onChange={setIncludeExpired}
                checkedChildren="Expired"
                unCheckedChildren="Expired"
              />
            </Space>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={loadData}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
          onChange={handleTableChange}
        />
        
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={currentPage || 1}
            pageSize={pageSize || 25}
            total={total || 0}
            showSizeChanger={false}
            showQuickJumper={false}
            showTotal={(total, range) => {
              const start = range && range[0] ? range[0] : 0;
              const end = range && range[1] ? range[1] : 0;
              return `${start}-${end} of ${total || 0} users`;
            }}
            onChange={(page) => {
              setCurrentPage(page || 1);
            }}
            onShowSizeChange={(current, size) => {
              setPageSize(size || 25);
              setCurrentPage(1);
            }}
          />
        </div>
      </Card>

      {/* Extend Trial Modal */}
      <Modal
        title={`Extend Trial for ${selectedUser?.email}`}
        open={extendModalVisible}
        onCancel={() => {
          setExtendModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleExtendTrial}
        >
          <Form.Item
            name="extension_days"
            label="Extension Days"
            rules={[
              { required: true, message: 'Please enter extension days' },
              { type: 'number', min: 1, max: 365, message: 'Must be between 1 and 365 days' }
            ]}
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              placeholder="Enter number of days"
            />
          </Form.Item>
          
          <Form.Item
            name="reason"
            label="Reason (Optional)"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter reason for extension..."
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setExtendModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Extend Trial
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* User Activity Drawer */}
      <Drawer
        title={`Activity for ${selectedUser?.email}`}
        placement="right"
        size="large"
        open={activityDrawerVisible}
        onClose={() => setActivityDrawerVisible(false)}
      >
        {userActivities.length > 0 ? (
          <Timeline>
            {userActivities.map((activity, index) => (
              <Timeline.Item key={index}>
                <div>
                  <Text strong>{trialUserService.formatActivityType(activity.activity_type)}</Text>
                  <br />
                  <Text type="secondary">
                    {new Date(activity.created_at).toLocaleString()}
                  </Text>
                  {activity.activity_data && Object.keys(activity.activity_data).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text code style={{ fontSize: '12px' }}>
                        {JSON.stringify(activity.activity_data, null, 2)}
                      </Text>
                    </div>
                  )}
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <div style={{ textAlign: 'center', marginTop: 50 }}>
            <Text type="secondary">No activity found for this user</Text>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default TrialUserManagement;
